import { useEffect, useState } from "react";
import { Mail, Plus } from "lucide-react";
import { supabase, CLINIC_ID } from "./supabase.js";
import { friendlyError } from "./lib/format.js";
import { inviteAppUserAccess, loadRoles } from "./data.js";

const emptyForm = {
  name:        "",
  username:    "",
  legacy_role: "Nakes",
  status:      "ACTIVE",
};

// Fallback hanya dipakai bila tabel roles gagal dibaca. Dalam kondisi normal,
// seluruh pilihan role berasal dari tabel roles.
const FALLBACK_ROLES = ["Admin", "Nakes", "Nakes Plus", "Kasir", "Frontline"];

function generateUserId() {
  return "USR-" + crypto.randomUUID().replace(/-/g, "").slice(0, 8).toUpperCase();
}

export function PenggunaPage() {
  const [rows, setRows]           = useState([]);
  const [roles, setRoles]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [form, setForm]           = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen]   = useState(false);
  const [saving, setSaving]       = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const [notice, setNotice]       = useState("");

  async function fetchUsers() {
    setLoading(true);
    setError("");
    try {
      const { data, error: e } = await supabase
        .from("users")
        .select("id,name,username,legacy_role,status,created_at")
        .order("name");
      if (e) throw e;
      setRows(data || []);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchUsers();
    loadRoles().then((r) => setRoles(r.map((x) => x.role_name))).catch(() => {});
  }, []);

  function updateField(key, val) { setForm((p) => ({ ...p, [key]: val })); }

  function resetForm() { setForm(emptyForm); setEditingId(null); setFormOpen(false); setError(""); }

  function startEdit(row) {
    setEditingId(row.id);
    setFormOpen(true);
    const normalizedRole = row.legacy_role === "Frontliner" && roles.includes("Frontline")
      ? "Frontline"
      : row.legacy_role;
    const knownRole = roles.includes(normalizedRole) || (!roles.length && FALLBACK_ROLES.includes(normalizedRole));
    setForm({
      name:        row.name        || "",
      username:    row.username    || "",
      legacy_role: knownRole ? normalizedRole : (roles[0] || FALLBACK_ROLES[0]),
      status:      row.status      || "ACTIVE",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const roleSource = roles.length
    ? [...roles, form.legacy_role]
    : [...FALLBACK_ROLES, form.legacy_role];
  const roleOptions = Array.from(new Set(roleSource.filter(Boolean)));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim())     { setError("Nama pengguna wajib diisi."); return; }
    if (!form.username.trim()) { setError("Username wajib diisi."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.username.trim())) {
      setError("Email login wajib format email yang valid, contoh: nama@gmail.com.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        p_user_id:    editingId || generateUserId(),
        name:        form.name.trim(),
        username:    form.username.trim().toLowerCase(),
        legacy_role: form.legacy_role,
        status:      form.status,
      };
      const { data: savedUser, error: se } = await supabase.rpc("admin_save_app_user", {
        p_user_id:     payload.p_user_id,
        p_name:        payload.name,
        p_username:    payload.username,
        p_legacy_role: payload.legacy_role,
        p_status:      payload.status,
        p_clinic_id:   CLINIC_ID,
      });
      if (se) throw se;

      const savedId = savedUser?.id || payload.p_user_id;
      let inviteMessage = "";
      if (!editingId) {
        try {
          const invite = await inviteAppUserAccess({ appUserId: savedId, email: payload.username });
          inviteMessage = invite?.status === "LINKED_RESET_SENT"
            ? `Link pengaturan password dikirim ke ${payload.username}.`
            : `Undangan login dikirim ke ${payload.username}.`;
        } catch (inviteError) {
          inviteMessage = `Pengguna tersimpan, tetapi undangan belum terkirim: ${friendlyError(inviteError)}. Gunakan tombol Kirim akses pada daftar.`;
        }
      }
      resetForm();
      setNotice(inviteMessage || (editingId ? "Data pengguna diperbarui." : "Data pengguna tersimpan."));
      await fetchUsers();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleInvite(row) {
    const email = String(row.username || "").trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Username pengguna ini belum berupa email yang valid. Edit pengguna dan isi email login terlebih dahulu.");
      return;
    }
    if (!window.confirm(`Kirim link akses login ke ${email}? Pengguna akan membuat password sendiri melalui email.`)) return;
    setInvitingId(row.id);
    setError("");
    setNotice("");
    try {
      const result = await inviteAppUserAccess({ appUserId: row.id, email });
      setNotice(result?.status === "RESET_SENT" || result?.status === "LINKED_RESET_SENT"
        ? `Link pengaturan password dikirim ke ${email}.`
        : `Undangan login dikirim ke ${email}.`);
      await fetchUsers();
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setInvitingId(null);
    }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Hapus pengguna "${row.name}" (${row.username})?`)) return;
    setError("");
    try {
      const { error: de } = await supabase.rpc("admin_delete_app_user", {
        p_user_id:   row.id,
        p_clinic_id: CLINIC_ID,
      });
      if (de) throw de;
      if (editingId === row.id) resetForm();
      await fetchUsers();
    } catch (err) {
      setError(friendlyError(err));
    }
  }

  return (
    <div className="page-stack">

      {error && !formOpen && (
        <div className="alert alert-danger">
          <strong>Terjadi kesalahan.</strong> {error}
        </div>
      )}
      {notice && !formOpen && (
        <div className="alert alert-success"><strong>Berhasil.</strong> {notice}</div>
      )}

      {/* ── Form tambah/edit, dibuka lewat tombol atau Edit ── */}
      {formOpen && (
        <article className="panel">
          <div className="panel-head">
            <div>
              <span className="eyebrow">Master Data XV</span>
              <h3>{editingId ? "Edit Pengguna" : "Tambah Pengguna"}</h3>
            </div>
            {!editingId && (
              <button type="button" className="button button-quiet" onClick={resetForm}>
                Batal
              </button>
            )}
          </div>

          {error && (
            <div className="alert alert-danger">
              <strong>Terjadi kesalahan.</strong> {error}
            </div>
          )}

          <form className="nakes-form" onSubmit={handleSubmit}>
          <div className="nf-row">
            <div className="nf-field" style={{ flex: 2 }}>
              <label>Nama Pengguna *</label>
              <input value={form.name} onChange={(e) => updateField("name", e.target.value)}
                placeholder="Nama lengkap" required />
            </div>
            <div className="nf-field" style={{ flex: 2 }}>
              <label>Email login *</label>
              <input value={form.username} onChange={(e) => updateField("username", e.target.value)}
                type="email" placeholder="nama@contoh.com" required />
            </div>
            <div className="nf-field" style={{ flex: 2 }}>
              <label>Akses password</label>
              <div className="nf-help">Password dibuat sendiri oleh pengguna melalui email undangan.</div>
            </div>
          </div>

          <div className="nf-row">
            <div className="nf-field" style={{ flex: 2 }}>
              <label>Role</label>
              <select value={form.legacy_role} onChange={(e) => updateField("legacy_role", e.target.value)}>
                {roleOptions.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <div className="nf-field" style={{ flex: 2 }}>
              <label>Status</label>
              <select value={form.status} onChange={(e) => updateField("status", e.target.value)}>
                <option value="ACTIVE">Aktif</option>
                <option value="INACTIVE">Nonaktif</option>
              </select>
            </div>
            <div className="nf-field" style={{ flex: 2 }}>
              <div className="nf-actions" style={{ paddingTop: 22 }}>
                <button type="submit" className="button button-primary" disabled={saving}>
                  {saving ? "Menyimpan…" : editingId ? "Perbarui" : "Simpan"}
                </button>
                {editingId && (
                  <button type="button" className="button button-quiet" onClick={resetForm}>Batal Edit</button>
                )}
              </div>
            </div>
          </div>
          </form>
        </article>
      )}

      {/* ── Tabel ── */}
      <article className="panel table-panel" style={{ padding: '1.5rem' }}>
        <div className="panel-head">
          <div>
            <span className="eyebrow">Manajemen Akses</span>
            <h3>Daftar Pengguna</h3>
          </div>
          {!formOpen && (
            <button
              type="button"
              className="button button-primary"
              onClick={() => { setError(""); setForm(emptyForm); setEditingId(null); setFormOpen(true); }}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              <Plus size={16} /> Tambah Pengguna
            </button>
          )}
        </div>

        {loading ? (
          <div className="content-loading">
            <div className="loader" />
            <span>Memuat data pengguna…</span>
          </div>
        ) : (
          <table className="mt-table">
            <thead>
              <tr>
                <th>NAMA</th>
                <th>USERNAME</th>
                <th>ROLE</th>
                <th>STATUS</th>
                <th>AKSI</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td><strong>{row.name}</strong></td>
                  <td style={{ color: "#6b6555", fontFamily: "monospace", fontSize: 13 }}>{row.username}</td>
                  <td>
                    <span className={`status ${
                      row.legacy_role === "Admin"        ? "status-danger"   :
                      row.legacy_role === "Nakes Plus"   ? "status-info"     :
                      row.legacy_role === "Nakes"        ? "status-positive" :
                      row.legacy_role === "Kasir"        ? "status-warning"  :
                      "status-neutral"
                    }`}>
                      {row.legacy_role || "—"}
                    </span>
                  </td>
                  <td>
                    <span className={`status ${row.status === "ACTIVE" ? "status-positive" : "status-danger"}`}>
                      {row.status === "ACTIVE" ? "Aktif" : "Nonaktif"}
                    </span>
                  </td>
                  <td>
                    <button className="link-button" onClick={() => startEdit(row)}>Edit</button>
                    {" "}
                    {row.status === "ACTIVE" && (
                      <button className="link-button" onClick={() => handleInvite(row)} disabled={invitingId === row.id}>
                        <Mail size={13} style={{ verticalAlign: "-2px" }} /> {invitingId === row.id ? "Mengirim…" : "Kirim akses"}
                      </button>
                    )}
                    {" "}
                    <button className="link-button link-danger" onClick={() => handleDelete(row)}>Hapus</button>
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={5} className="muted" style={{ padding: "16px 12px" }}>Belum ada pengguna.</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </article>

      <style>{`
        .nakes-form { display: flex; flex-direction: column; gap: 0; }
        .nf-row { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 14px; }
        .nf-field { flex: 1; min-width: 160px; display: flex; flex-direction: column; gap: 6px; }
        .nf-field label { font-size: 12px; font-weight: 600; color: #6b6555; }
        .nf-help { min-height: 36px; display: flex; align-items: center; color: #6b6555; font-size: 13px; line-height: 1.35; }
        .nf-field input, .nf-field select {
          padding: 9px 12px; border: 1px solid #e8e2d5; border-radius: 8px;
          font-size: 14px; background: #fff; width: 100%; box-sizing: border-box;
        }
        .nf-actions { display: flex; gap: 10px; }
        .mt-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .mt-table th {
          text-align: left; font-size: 11px; text-transform: uppercase;
          letter-spacing: 0.04em; color: #7a7364; padding: 10px 12px;
          border-bottom: 1px solid #e8e2d5;
        }
        .mt-table td { padding: 12px; border-bottom: 1px solid #f1ece0; }
        .link-button { background: none; border: none; padding: 0; font-size: 13px; font-weight: 600; color: #2f6f4f; cursor: pointer; }
        .link-danger { color: #c0392b; }
      `}</style>
    </div>
  );
}
